import {
  CREATE_ENTRY_FIELD_RULES,
  CREATE_ENTRY_FIELD_KEYS,
  type FieldRequirement,
} from 'mcp-server/categoryFieldRules';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

/**
 * AOZONE-104: the per-category `create_entry` field rules, RENDERED from
 * `CREATE_ENTRY_FIELD_RULES` — which AOZONE-103 generates from
 * `ENTRY_CATEGORIES` + `CATEGORY_FIELD_SPEC` in slack-bot's `modalView.ts`, the
 * source of truth (CLAUDE.md, AOZONE-36). Nothing here transcribes a category
 * name or a field rule, and the rows are rendered in the array's canonical order
 * rather than re-sorted.
 */

/** Rendered when a category has no fields in a bucket. A word, not a dash: a
 * dash reads as silence in a screen reader. */
const EMPTY_BUCKET = 'None';

/**
 * Bucket → user-facing label and badge variant. A TOTAL map keyed on
 * `FieldRequirement`, so a fourth requirement state added upstream is a compile
 * error here instead of an unlabeled render.
 */
const BUCKETS: ReadonlyArray<{
  key: FieldRequirement;
  label: string;
  variant: 'default' | 'secondary' | 'outline';
}> = [
  { key: 'required', label: 'Required', variant: 'default' },
  { key: 'optional', label: 'Optional', variant: 'secondary' },
  { key: 'notApplicable', label: 'Not applicable', variant: 'outline' },
];

export function McpCategoryRules() {
  return (
    <div>
      <p className="text-sm text-slate-600">
        Which arguments <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">
          create_entry
        </code>{' '}
        accepts depends on the entry category. <strong>Not applicable</strong> fields are
        rejected, not ignored. <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">
          category
        </code>{' '}
        is always required, and{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">mbrPeriod</code>{' '}
        (YYYY-MM, UTC) is always optional and defaults to the current month. The{' '}
        {CREATE_ENTRY_FIELD_KEYS.length} entry fields fall out per category as:
      </p>

      <ul className="mt-4 space-y-4">
        {CREATE_ENTRY_FIELD_RULES.map((rule, index) => (
          <li key={rule.category}>
            {index > 0 && <Separator className="mb-4" />}
            <div data-testid="mcp-rule-category" className="text-sm font-medium text-slate-800">
              {rule.category}
            </div>
            <dl className="mt-2 space-y-1">
              {BUCKETS.map(({ key, label, variant }) => (
                <div key={key} className="flex flex-wrap items-baseline gap-2">
                  <dt>
                    <Badge variant={variant}>{label}</Badge>
                  </dt>
                  <dd
                    data-testid={`mcp-rule-${key}`}
                    className="font-mono text-xs text-slate-600"
                  >
                    {rule[key].join(', ') || EMPTY_BUCKET}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
